import { Helmet } from 'react-helmet-async';
import { motion } from '../lib/motion';
import { ArrowRight, Phone, CircleCheck } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { HomepageContent } from '../lib/types';
import { SITE_IMAGES } from '../data/images';
import { REGISTRE } from '../data/registre';
import DevisForm from './DevisForm';

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } },
};

const TRUST_ITEMS = [
  REGISTRE['devis.delai'].court,
  'Devis 100% gratuit',
  REGISTRE['assurance.incluse'].court,
  REGISTRE['stats.anciennete'].court,
];

// Le bloc « Estimation rapide » — trois champs hors de tout <form>, dont le
// bouton n'était qu'un lien vers /contact-devis — n'existe plus. La colonne
// droite porte désormais LE formulaire de devis, DevisForm, le même que
// /contact-devis : ce qui est saisi ici part directement, sans page
// intermédiaire. Sous `lg`, la carte n'est pas rendue : un bouton fait
// défiler vers le formulaire de pied de page, que chaque page porteuse du
// hero rend aussi (ContactForm, ancre `#devis`).

interface Props {
  data?: HomepageContent['hero'] | null;
  /**
   * Nom de la ville à afficher dans le H1, pour les pages locales.
   *
   * Absent (cas de l'accueil), le Hero rend exactement le contenu actuel :
   * c'est la seule façon de réutiliser ce composant sur les pages communes
   * sans en maintenir une seconde copie qui divergerait à la première
   * retouche graphique.
   */
  cityName?: string;
  /**
   * Codes postaux de la commune, accolés au nom dans le H1.
   *
   * C'est la seule donnée réellement discriminante qu'on puisse loger dans un
   * titre sans le rallonger : « déménagement 4950 » est une requête réelle, et
   * le code postal distingue soixante-dix H1 que « Votre déménagement à X »
   * laissait identiques à un mot près.
   */
  codesPostaux?: string[];
  /**
   * Ligne d'accroche. Absente, le hero garde la phrase de l'accueil. Les pages
   * communes en passent une composée à partir de leurs propres mesures —
   * distance, durée, sections — voir `accrocheHero` dans CommuneLandingPage.
   */
  accroche?: string;
  /**
   * Texte alternatif de la photo du hero.
   *
   * La photo est une VRAIE photo d'un camion Gramme, hébergée sur le domaine
   * (voir src/data/images.ts) : elle porte du sens, et elle était servie en
   * `alt=""` avec `aria-hidden` sur soixante-douze pages, comme si c'était
   * un fond décoratif. Un audit on-page l'a relevée comme seule anomalie du
   * site.
   *
   * Absent, l'alt de l'accueil s'applique. Les pages communes passent le
   * leur, qui nomme la commune : c'est la seule chose qu'un `alt` de gabarit
   * puisse dire de vrai — le camion est celui de Gramme, le service est
   * rendu dans la commune, et la photo n'a été prise ni ici ni là.
   */
  imageAlt?: string;
}

export default function HeroSection({ data, cityName, codesPostaux, accroche, imageAlt }: Props) {
  const alt = imageAlt ?? SITE_IMAGES.hero.alt;
  // Surcharge éventuelle depuis le CMS : une URL unique, donc sans srcset.
  const override = data?.background_image;

  // Liège reste la valeur par défaut : l'accueil ne passe pas de prop et son
  // rendu doit rester au caractère près celui d'aujourd'hui.
  const ville = cityName?.trim() || 'Liège';
  const codes = codesPostaux?.length ? ` (${codesPostaux.join(' · ')})` : '';

  return (
    <section id="accueil" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Vraie balise <img> et non un background-image CSS.
          Un background en CSS n'est découvert par le navigateur qu'une fois la
          feuille de style chargée et l'élément mis en page — d'où 2790 ms de
          délai avant même le début du téléchargement (mesuré sur PageSpeed).
          Une <img> présente dans le HTML pré-rendu est découverte tout de
          suite, accepte fetchpriority="high", et laisse le navigateur choisir
          une seule largeur via srcset au lieu d'en télécharger plusieurs.

          <picture> ajoute la négociation de format : AVIF si le navigateur le
          gère (19 à 31 Kio sur mobile), sinon WebP, sinon JPEG. */}
      {/* Préchargement de l'image LCP.
          ────────────────────────────────────────────────────────────────────
          L'audit du 30/08 mesure 430 ms de « délai de chargement de la
          ressource » : le serveur répond en 146 ms, mais l'image n'est demandée
          qu'une fois le HTML analysé. `fetchpriority="high"` fixe la priorité,
          il ne change pas le moment de la découverte.

          POURQUOI CE PRELOAD-CI ET PAS CELUI QUI AVAIT ÉTÉ RETIRÉ

          Un preload avait existé, figé sur la variante 1200 px : sur mobile il
          faisait télécharger DEUX images, celle du preload et celle que le
          rendu choisissait à 640 px. Il avait été retiré pour cette raison, et
          c'était juste.

          `imageSrcSet` + `imageSizes` répondent exactement à cette objection :
          le navigateur applique la même sélection qu'à la balise, et précharge
          la variante qu'il aurait prise de toute façon. Une image, pas deux.

          `type="image/avif"` cantonne le preload aux navigateurs qui gèrent
          l'AVIF — les autres l'ignorent entièrement plutôt que de précharger un
          format qu'ils n'utiliseront pas. Ils perdent le gain, pas la page.

          Posé par Helmet et non dans index.html : le hero ne vit que sur les
          pages qui montent ce composant, et précharger sa photo sur les 95
          pages ferait payer à 94 d'entre elles une image qu'elles n'affichent
          pas. */}
      {!override && (
        <Helmet>
          <link
            rel="preload"
            as="image"
            type="image/avif"
            imageSrcSet={SITE_IMAGES.hero.srcSetAvif}
            imageSizes="100vw"
            fetchPriority="high"
          />
        </Helmet>
      )}
      {override ? (
        <img
          src={override}
          alt={alt}
          fetchPriority="high"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <picture>
          <source type="image/avif" srcSet={SITE_IMAGES.hero.srcSetAvif} sizes="100vw" />
          <source type="image/webp" srcSet={SITE_IMAGES.hero.srcSetWebp} sizes="100vw" />
          {/* `decoding="async"` retiré, et `width`/`height` posés.
              ────────────────────────────────────────────────────────────────
              `decoding="async"` autorise le navigateur à PEINDRE LA PAGE SANS
              ATTENDRE que l'image soit décodée. Sur une image décorative c'est
              exactement ce qu'on veut ; sur l'élément LCP, c'est le contraire :
              il autorise à repousser d'une frame ou plus l'apparition du seul
              pixel que la métrique mesure. L'audit du 07/09/2026 relève 1740 ms
              de « délai d'affichage de l'élément » pour une ressource prête
              bien avant. `decoding="sync"` n'est pas la réponse non plus — il
              bloquerait le fil principal. L'absence d'attribut laisse le
              navigateur choisir (`auto`), et il privilégie alors la peinture de
              l'image prioritaire.

              `width`/`height` déclarent le rapport de forme intrinsèque de la
              photo (1200 × 900, soit 4:3). Ils ne changent RIEN à la mise en
              page — `absolute inset-0 w-full h-full object-cover` la pilote
              entièrement, et le CLS mesuré reste à 0 — mais ils donnent au
              navigateur de quoi réserver la boîte avant l'arrivée de l'image
              plutôt que de la découvrir au décodage. */}
          <img
            src={SITE_IMAGES.hero.src}
            srcSet={SITE_IMAGES.hero.srcSetJpeg}
            sizes="100vw"
            alt={alt}
            width={1200}
            height={900}
            fetchPriority="high"
            className="absolute inset-0 w-full h-full object-cover"
          />
        </picture>
      )}
      {/* Voile bleu uniforme, à l'identique de l'existant : c'est lui qui
          donne au titre blanc son contraste franc sur toute la largeur. Un
          dégradé s'allégeant à droite avait été essayé pour mieux montrer le
          camion, mais il dégradait la lisibilité — la lisibilité primait. */}
      <div className="absolute inset-0 bg-[#0C2094]/75" />

      <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-8 py-24 grid grid-cols-1 lg:grid-cols-[55%_45%] gap-10 lg:gap-16 items-center">

        {/* — COLONNE GAUCHE — */}
        <motion.div variants={container} initial="hidden" animate="visible" className="flex flex-col gap-6">

          <motion.div variants={fadeUp} className="flex items-center gap-2">
            <span className="bg-yellow text-navy text-[13px] font-bold rounded-full px-3 py-1 border border-yellow/60">
              {/* Le chiffre de satisfaction a été RETIRÉ de cette pastille.
                  Personne n'a pu dire d'où venait le « supérieure à 95% », et
                  la section avis de cette même page affiche 4,0/5 sur Google —
                  soit 80%. Un lecteur qui fait la soustraction conclut que le
                  site enjolive : le chiffre coûtait plus qu'il ne rapportait.

                  Il n'est pas perdu. Il reste déclaré dans le registre, et
                  reviendra ici tout seul le jour où Gramme dira d'où il sort —
                  « enquête interne 2025, N répondants » se défend très bien à
                  côté d'une note Google. Voir `stats.satisfaction` et
                  `affichable()`. */}
              {REGISTRE['entreprise.qualite'].court} · {REGISTRE['stats.anciennete'].court}
            </span>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-[2.2rem] sm:text-5xl md:text-6xl leading-[1.08] font-black uppercase text-white"
          >
            Votre déménagement <br />
            {/* Un seul enfant texte (gabarit littéral) et non `à {ville}` :
                deux enfants adjacents feraient insérer à React un séparateur
                <!-- --> dans le HTML pré-rendu, modifiant l'accueil sans
                raison. Le traitement visuel — pastille jaune sur fond navy —
                est celui déjà en place, plutôt qu'un second style jaune sur
                bleu qui n'existe nulle part ailleurs sur le site. */}
            <span className="bg-yellow text-navy px-2 py-0.5 inline-block mt-1">{`à ${ville}${codes}`}</span>{' '}
            <span className="text-white">&amp; partout</span> <br />
            <span className="text-white/90">en Belgique</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="text-white/85 text-lg md:text-xl max-w-[520px] leading-relaxed">
            {accroche ?? (
              <>
                Devis gratuit en 24h. Assurance incluse.<br className="hidden sm:block" />
                Sans engagement. Depuis 1948.
              </>
            )}
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4">
            {/* Sous `lg`, le formulaire du hero n'est pas rendu : le bouton
                fait défiler jusqu'au formulaire de pied de page de la même
                page (`#devis`, dans ContactForm). À partir de `lg`, le
                formulaire est à droite et le bouton garde sa cible d'origine. */}
            <a
              href="#devis"
              className="lg:hidden bg-yellow text-navy font-bold uppercase rounded-lg py-4 px-8 flex items-center justify-center gap-2 border border-yellow/60 hover:bg-white hover:border-white hover:scale-[1.02] transition-all duration-200 shadow-lg shadow-yellow/20 text-base animate-[pulse-cta_3s_ease-in-out_infinite]"
            >
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5" />
            </a>
            <Link
              to="/contact-devis"
              className="hidden lg:flex bg-yellow text-navy font-bold uppercase rounded-lg py-4 px-8 items-center justify-center gap-2 border border-yellow/60 hover:bg-white hover:border-white hover:scale-[1.02] transition-all duration-200 shadow-lg shadow-yellow/20 text-base animate-[pulse-cta_3s_ease-in-out_infinite]"
            >
              Demander un devis gratuit
              <ArrowRight className="w-5 h-5" />
            </Link>
            <a
              href="tel:+3242645016"
              className="border-2 border-white/70 text-white font-bold uppercase rounded-lg py-4 px-8 flex items-center justify-center gap-2 hover:bg-white hover:text-navy transition-all duration-200 text-base"
            >
              <Phone className="w-5 h-5" />
              04 264 50 16
            </a>
          </motion.div>

          <motion.div variants={fadeUp} className="flex flex-wrap gap-x-5 gap-y-2 pt-2 border-t border-white/15">
            {TRUST_ITEMS.map((item) => (
              <div key={item} className="flex items-center gap-1.5 text-white/80 text-sm">
                <CircleCheck className="w-4 h-4 text-yellow flex-shrink-0" />
                {item}
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* — COLONNE DROITE : le formulaire de devis, en carte —
            `hidden lg:block` : réservé au bureau. Sur mobile, le bouton de la
            colonne gauche mène au formulaire de pied de page. */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8, delay: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
          className="hidden lg:block"
        >
          <DevisForm
            source="hero"
            variante="carte"
            prefixeId="hero"
            ancre="devis-hero"
            surtitre="Devis gratuit en 24h"
            titre={cityName ? `Votre devis à ${ville}` : 'Demandez votre devis gratuit'}
            villeParDefaut={cityName}
          />
        </motion.div>

      </div>
    </section>
  );
}
